/**
 * Loading-overlay state machine.
 *
 * Replaces the previous CSS-class-juggling that lived across four
 * functions (`showLoading`, `hideLoading`, `showLoadingError`,
 * `setLoadingLabel`). The earlier code path had a latent bug:
 * `showLoading()` did NOT clear `is-error`, so a retry after an error
 * landed with the spinner+label visible AND the red error styling
 * still applied (the `.editor-loading-overlay.is-error` rule turned
 * the spinner red and paused its animation).
 *
 * One function (`setOverlayState`) owns every class assignment as a
 * unit. Each enum value maps to exactly one class set; the function
 * re-applies the full set on every transition, which makes
 * `is-error` impossible to "leak" into a non-error state.
 *
 * Pattern mirrors `JobStateMachine` from web/pipeline.py.
 */
// Closed-set state vocabulary. Typed as `as const` so each member is a
// literal `LoadingOverlayStateValue` (not `LoadingOverlayStateValue | undefined`
// which the wider `Record<string, X>` annotation would yield under
// noUncheckedIndexedAccess — making the computed-property-name uses below
// fail with TS2464).
export const LoadingOverlayState = Object.freeze({
    HIDDEN: "hidden",
    LOADING: "loading",
    HIDING: "hiding",
    ERROR: "error",
});
// WHY: Record<string, ...> annotation is intentional — this table is
// keyed by this._state: string at runtime (not a closed-key enum). The T|undefined
// return under noUncheckedIndexedAccess is correct; callsites guard with !valid.
const TRANSITIONS = Object.freeze({
    [LoadingOverlayState.HIDDEN]: new Set([
        LoadingOverlayState.LOADING,
    ]),
    [LoadingOverlayState.LOADING]: new Set([
        LoadingOverlayState.HIDING,
        LoadingOverlayState.ERROR,
        LoadingOverlayState.HIDDEN,
        LoadingOverlayState.LOADING,
    ]),
    [LoadingOverlayState.HIDING]: new Set([
        LoadingOverlayState.HIDDEN,
        LoadingOverlayState.LOADING,
        LoadingOverlayState.ERROR,
    ]),
    [LoadingOverlayState.ERROR]: new Set([
        LoadingOverlayState.HIDDEN,
        LoadingOverlayState.LOADING,
    ]),
});
function applyClasses(overlayEl, target) {
    if (!overlayEl)
        return;
    const cl = overlayEl.classList;
    // Bulk reset: every transition re-applies the full class set so no
    // class from a prior state can leak (the pre-fix showLoading-after-error
    // bug left `is-error` because it only removed `hidden` and `is-hiding`).
    cl.remove("hidden", "is-hiding", "is-error");
    switch (target) {
        case LoadingOverlayState.HIDDEN:
            cl.add("hidden");
            break;
        case LoadingOverlayState.LOADING:
            break;
        case LoadingOverlayState.HIDING:
            cl.add("is-hiding");
            break;
        case LoadingOverlayState.ERROR:
            cl.add("is-error");
            break;
        default:
            throw new Error(`[overlay-state] unknown target: ${target}`);
    }
}
export class LoadingOverlayController {
    _overlayEl;
    _labelEl;
    _onEnter;
    _state;
    constructor(cfg) {
        if (!cfg?.overlayEl) {
            throw new Error("LoadingOverlayController requires overlayEl");
        }
        this._overlayEl = cfg.overlayEl;
        this._labelEl = cfg.labelEl ?? null;
        this._onEnter = cfg.onEnter ?? {};
        this._state = cfg.initial ??
            (this._overlayEl.classList.contains("hidden")
                ? LoadingOverlayState.HIDDEN
                : LoadingOverlayState.LOADING);
    }
    get state() { return this._state; }
    is(target) { return this._state === target; }
    setState(target, opts = {}) {
        const valid = TRANSITIONS[this._state];
        if (!valid?.has(target)) {
            throw new Error(`[overlay-state] illegal transition ${this._state} -> ${target}. ` +
                `Valid: ${valid ? Array.from(valid).join(", ") : "<none>"}`);
        }
        applyClasses(this._overlayEl, target);
        if (opts.label && this._labelEl) {
            this._labelEl.textContent = opts.label;
        }
        const prev = this._state;
        this._state = target;
        const hook = this._onEnter[target];
        if (hook)
            hook(prev, target);
    }
    setLabel(label) {
        if (this._labelEl && label)
            this._labelEl.textContent = label;
    }
}
